import { call, put, takeEvery } from 'redux-saga/effects'
import {
  ArchiveCandidate,
  CreateOpportunity,
  FetchCandidateCounts,
  FetchCandidates,
  FetchOpportunities,
  OfferCandidates,
  UnarchiveCandidate,
  UpdateOpportunity,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import { OpportunityStatus } from 'src/constants/opportunity/enums'
import {
  CandidateFilters,
  Opportunity,
  OpportunityCandidate,
  OpportunityCandidateCounts,
  OpportunityOfferResult,
} from 'src/interfaces/opportunity'
import { AsyncAction } from 'src/utils/reduxUtils'
import { ApiService, callSecureApi } from './api/callApi'

interface FetchOpportunitiesResponse {
  data: Opportunity[]
}

interface FetchCandidatesRequest extends CandidateFilters {
  limit: number
  offset: number
  sortColumn?: string
  sortDirection?: 'ASC' | 'DESC'
}

interface FetchCandidatesResponse {
  data: OpportunityCandidate[]
  total: number
}

interface CreateOpportunityRequest {
  title: string
  description: string
  partnerMarketIds: string[]
}

interface UpdateOpportunityRequest {
  opportunityId: string
  title?: string
  description?: string
  status?: OpportunityStatus
  partnerMarketIds?: string[]
}

interface OfferCandidatesRequest {
  candidateIds: string[]
  collectDataTemplateId: string
}

/**
 * Drops keys the user has not set so the query string stays clean and the backend applies its own defaults.
 */
const compactQuery = <T extends object>(input: T): Record<string, any> =>
  Object.entries(input).reduce<Record<string, any>>((acc, [key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value
    }

    return acc
  }, {})

function* onFetchOpportunities(action: AsyncAction<void, FetchOpportunitiesResponse>) {
  try {
    const { payload }: { payload: FetchOpportunitiesResponse } = yield call(
      callSecureApi,
      Endpoints.FetchOpportunities,
      { method: 'GET', apiService: ApiService.Paraworks },
    )

    yield put(FetchOpportunities.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    yield put(FetchOpportunities.failed(err))

    if (action.next) {
      action.next(err?.payload?.message || 'Failed to load opportunities')
    }
  }
}

function* onCreateOpportunity(action: AsyncAction<CreateOpportunityRequest, Opportunity>) {
  try {
    const { payload }: { payload: Opportunity } = yield call(
      callSecureApi,
      Endpoints.CreateOpportunity,
      { method: 'POST', apiService: ApiService.Paraworks, body: action.payload },
    )

    yield put(CreateOpportunity.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    yield put(CreateOpportunity.failed(err))

    if (action.next) {
      action.next(err?.payload?.message || 'Failed to create the opportunity')
    }
  }
}

function* onUpdateOpportunity(action: AsyncAction<UpdateOpportunityRequest, Opportunity>) {
  const { opportunityId, ...body } = action.payload

  try {
    const { payload }: { payload: Opportunity } = yield call(
      callSecureApi,
      Endpoints.UpdateOpportunity,
      {
        method: 'PATCH',
        apiService: ApiService.Paraworks,
        body,
        params: [{ field: ':opportunityId', value: opportunityId }],
      },
    )

    yield put(UpdateOpportunity.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    yield put(UpdateOpportunity.failed(err))

    if (action.next) {
      action.next(err?.payload?.message || 'Failed to update the opportunity')
    }
  }
}

function* onFetchCandidates(action: AsyncAction<FetchCandidatesRequest, FetchCandidatesResponse>) {
  try {
    const { payload }: { payload: FetchCandidatesResponse } = yield call(
      callSecureApi,
      Endpoints.FetchCandidates,
      { method: 'GET', apiService: ApiService.Paraworks, query: compactQuery(action.payload) },
    )

    yield put(FetchCandidates.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    yield put(FetchCandidates.failed(err))

    if (action.next) {
      action.next(err?.payload?.message || 'Failed to load candidates')
    }
  }
}

function* onFetchCandidateCounts(
  action: AsyncAction<CandidateFilters, OpportunityCandidateCounts>,
) {
  try {
    const { payload }: { payload: OpportunityCandidateCounts } = yield call(
      callSecureApi,
      Endpoints.FetchCandidateCounts,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
        query: compactQuery(action.payload || {}),
      },
    )

    yield put(FetchCandidateCounts.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    yield put(FetchCandidateCounts.failed(err))

    if (action.next) {
      action.next(err?.payload?.message || 'Failed to load candidate counts')
    }
  }
}

function* onOfferCandidates(action: AsyncAction<OfferCandidatesRequest, OpportunityOfferResult>) {
  try {
    const { payload }: { payload: OpportunityOfferResult } = yield call(
      callSecureApi,
      Endpoints.OfferCandidates,
      { method: 'POST', apiService: ApiService.Paraworks, body: action.payload },
    )

    yield put(OfferCandidates.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    yield put(OfferCandidates.failed(err))

    if (action.next) {
      action.next(err?.payload?.message || 'Failed to send the offers')
    }
  }
}

function* onArchiveCandidate(action: AsyncAction<{ candidateId: string }, OpportunityCandidate>) {
  try {
    const { payload }: { payload: OpportunityCandidate } = yield call(
      callSecureApi,
      Endpoints.ArchiveCandidate,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        params: [{ field: ':candidateId', value: action.payload.candidateId }],
      },
    )

    yield put(ArchiveCandidate.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    yield put(ArchiveCandidate.failed(err))

    if (action.next) {
      action.next(err?.payload?.message || 'Failed to archive the candidate')
    }
  }
}

function* onUnarchiveCandidate(action: AsyncAction<{ candidateId: string }, OpportunityCandidate>) {
  try {
    const { payload }: { payload: OpportunityCandidate } = yield call(
      callSecureApi,
      Endpoints.UnarchiveCandidate,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        params: [{ field: ':candidateId', value: action.payload.candidateId }],
      },
    )

    yield put(UnarchiveCandidate.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    yield put(UnarchiveCandidate.failed(err))

    if (action.next) {
      action.next(err?.payload?.message || 'Failed to restore the candidate')
    }
  }
}

export function* opportunitySaga() {
  yield takeEvery(FetchOpportunities.type.REQUEST, onFetchOpportunities)
  yield takeEvery(CreateOpportunity.type.REQUEST, onCreateOpportunity)
  yield takeEvery(UpdateOpportunity.type.REQUEST, onUpdateOpportunity)
  yield takeEvery(FetchCandidates.type.REQUEST, onFetchCandidates)
  yield takeEvery(FetchCandidateCounts.type.REQUEST, onFetchCandidateCounts)
  yield takeEvery(OfferCandidates.type.REQUEST, onOfferCandidates)
  yield takeEvery(ArchiveCandidate.type.REQUEST, onArchiveCandidate)
  yield takeEvery(UnarchiveCandidate.type.REQUEST, onUnarchiveCandidate)
}
